import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import Footer from './Footer';
import {
    Shield,
    FileText,
    User,
    Calendar,
    Mail,
    Phone,
    MapPin,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Trash2,
    Save
} from 'lucide-react';
import { apiClient, ConsentFormData, ConsentSubmissionData } from '../services/api';
import { validate } from '../utils/validation';

interface ConsentFormPageProps { }

const ConsentFormPage: React.FC<ConsentFormPageProps> = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const signatureRef = useRef<SignatureCanvas>(null);
    const signatureContainerRef = useRef<HTMLDivElement>(null);

    const [canvasWidth, setCanvasWidth] = useState(500);
    const [consentData, setConsentData] = useState<ConsentFormData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
    const [withdrawalReason, setWithdrawalReason] = useState('');

    // Editable contact details
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [city, setCity] = useState('');
    const [postcode, setPostcode] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Resize canvas to match container width
    useEffect(() => {
        const updateCanvasWidth = () => {
            if (signatureContainerRef.current) {
                const containerWidth = signatureContainerRef.current.offsetWidth;
                setCanvasWidth(containerWidth);
            }
        };

        updateCanvasWidth();

        const resizeObserver = new ResizeObserver(updateCanvasWidth);
        if (signatureContainerRef.current) {
            resizeObserver.observe(signatureContainerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    // Load consent form data
    useEffect(() => {
        const loadConsentForm = async () => {
            if (!token) {
                setError('Invalid consent link');
                setIsLoading(false);
                return;
            }

            try {
                const data = await apiClient.getConsentForm(token);
                setConsentData(data);

                // Initialize editable fields with current driver data
                if (data.driver) {
                    setEmail(data.driver.email || '');
                    setPhone(data.driver.phone || '');
                    setAddressLine1(data.driver.addressLine1 || '');
                    setAddressLine2(data.driver.addressLine2 || '');
                    setCity(data.driver.city || '');
                    setPostcode(data.driver.postcode || '');
                }

            } catch (err: any) {
                setError(err.message || 'Failed to load consent form');
            } finally {
                setIsLoading(false);
            }
        };

        loadConsentForm();
    }, [token]);

    // Handle signature drawing
    const handleSignatureBegin = () => {
        setHasSignature(true);
    };

    const handleSignatureEnd = () => {
        if (signatureRef.current?.isEmpty()) {
            setHasSignature(false);
        }
    };

    // Clear signature
    const clearSignature = () => {
        signatureRef.current?.clear();
        setHasSignature(false);
    };

    // Validate contact fields before submit
    const validateContactFields = (): boolean => {
        const errs: Record<string, string> = {};
        const emailErr = validate.required(email, 'Email') ?? validate.email(email);
        if (emailErr) errs.email = emailErr;
        const postcodeErr = validate.required(postcode, 'Postcode') ?? validate.ukPostcode(postcode);
        if (postcodeErr) errs.postcode = postcodeErr;
        const addressErr = validate.required(addressLine1, 'Address line 1');
        if (addressErr) errs.addressLine1 = addressErr;
        const cityErr = validate.required(city, 'City');
        if (cityErr) errs.city = cityErr;
        if (phone) {
            const phoneErr = validate.ukPhone(phone);
            if (phoneErr) errs.phone = phoneErr;
        }
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // Submit consent
    const handleSubmitConsent = async () => {
        if (!token || !signatureRef.current || !hasSignature) return;
        if (!validateContactFields()) return;

        try {
            setIsSubmitting(true);
            setError(null);

            // Get signature as base64 data URL
            const signatureDataURL = signatureRef.current.toDataURL();

            // Check if any contact details have changed
            const driver = consentData?.driver;
            const hasChanges = driver && (
                email !== driver.email ||
                phone !== (driver.phone || '') ||
                addressLine1 !== driver.addressLine1 ||
                addressLine2 !== (driver.addressLine2 || '') ||
                city !== driver.city ||
                postcode !== driver.postcode
            );

            const submissionData: ConsentSubmissionData = {
                signature: signatureDataURL,
                ipAddress: undefined, // Backend will capture this
                userAgent: navigator.userAgent,
                ...(hasChanges && {
                    updatedDriverData: {
                        email,
                        phone: phone || undefined,
                        addressLine1,
                        addressLine2: addressLine2 || undefined,
                        city,
                        postcode
                    }
                })
            };

            await apiClient.submitConsent(token, submissionData);
            setSuccess(true);

            // Redirect to success page after 3 seconds
            setTimeout(() => {
                navigate('/consent/success');
            }, 3000);

        } catch (err: any) {
            setError(err.message || 'Failed to submit consent');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle consent withdrawal
    const handleWithdrawConsent = async () => {
        if (!token) return;

        try {
            setIsSubmitting(true);
            setError(null);

            await apiClient.withdrawConsent(token, withdrawalReason.trim() || 'No reason provided');
            setShowWithdrawDialog(false);

            // Show withdrawal success and redirect
            toast.success('Consent has been withdrawn successfully.');
            navigate('/consent/withdrawn');

        } catch (err: any) {
            setError(err.message || 'Failed to withdraw consent');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin h-12 w-12 border-4 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
                        <p className="text-gray-600">Loading consent form...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Error state
    if (error && !consentData) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <div className="flex-1 flex items-center justify-center">
                    <div className="max-w-md mx-auto text-center">
                        <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Form Unavailable</h1>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <p className="text-sm text-gray-500">
                            This consent form may have expired or already been completed.
                        </p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <div className="flex-1 flex items-center justify-center">
                    <div className="max-w-md mx-auto text-center">
                        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Consent Submitted Successfully</h1>
                        <p className="text-gray-600 mb-4">
                            Thank you for providing your D906 consent. Your information is now secure and compliant.
                        </p>
                        <p className="text-sm text-gray-500">
                            This consent is valid for 3 years from today ({format(new Date(), 'dd/MM/yyyy')}).
                        </p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    const driver = consentData?.driver;
    const organisation = consentData?.organisation;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <div className="flex-1 py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-center mb-4">
                        <Shield className="w-8 h-8 mr-3" style={{ color: '#69BE28' }} />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">DVLA Consent Form (D906)</h1>
                            <p className="text-gray-600">Digital consent for licence checking</p>
                        </div>
                    </div>

                    {/* Organisation Info */}
                    <div className="rounded-md p-4" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                        <h3 className="font-semibold mb-2" style={{ color: '#231f20' }}>Requested by:</h3>
                        <p className="font-medium" style={{ color: '#69BE28' }}>{organisation?.name}</p>
                        <div className="flex items-center text-sm mt-1" style={{ color: '#69BE28' }}>
                            <Mail className="w-4 h-4 mr-1" />
                            {organisation?.contactEmail}
                            {organisation?.contactPhone && (
                                <>
                                    <Phone className="w-4 h-4 ml-3 mr-1" />
                                    {organisation.contactPhone}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Driver Information */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <User className="w-5 h-5 mr-2" />
                        Your Information
                    </h2>

                    <p className="text-sm text-gray-600 mb-4">
                        Please verify and update your contact details if needed. Name, date of birth, and licence number cannot be changed here.
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                        {/* Read-only fields */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Full Name</label>
                            <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{driver?.firstName} {driver?.lastName}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Licence Number</label>
                            <p className="text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded-md">{driver?.licenceNumber}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                            <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                                {driver?.dateOfBirth ? format(new Date(driver.dateOfBirth), 'dd/MM/yyyy') : 'Not provided'}
                            </p>
                        </div>

                        {/* Editable fields */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email *</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors(p => ({ ...p, email: undefined as any })); }}
                                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 ${fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ outlineColor: '#69BE28' }}
                            />
                            {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => { setPhone(e.target.value); if (fieldErrors.phone) setFieldErrors(p => ({ ...p, phone: undefined as any })); }}
                                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 ${fieldErrors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ outlineColor: '#69BE28' }}
                                placeholder="Optional"
                            />
                            {fieldErrors.phone && <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>}
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 flex items-center">
                                <MapPin className="w-4 h-4 mr-1" />
                                Address Line 1 *
                            </label>
                            <input
                                type="text"
                                value={addressLine1}
                                onChange={(e) => { setAddressLine1(e.target.value); if (fieldErrors.addressLine1) setFieldErrors(p => ({ ...p, addressLine1: undefined as any })); }}
                                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 ${fieldErrors.addressLine1 ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ outlineColor: '#69BE28' }}
                            />
                            {fieldErrors.addressLine1 && <p className="mt-1 text-sm text-red-600">{fieldErrors.addressLine1}</p>}
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                            <input
                                type="text"
                                value={addressLine2}
                                onChange={(e) => setAddressLine2(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50"
                                style={{ outlineColor: '#69BE28' }}
                                placeholder="Optional"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">City *</label>
                            <input
                                type="text"
                                value={city}
                                onChange={(e) => { setCity(e.target.value); if (fieldErrors.city) setFieldErrors(p => ({ ...p, city: undefined as any })); }}
                                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 ${fieldErrors.city ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ outlineColor: '#69BE28' }}
                            />
                            {fieldErrors.city && <p className="mt-1 text-sm text-red-600">{fieldErrors.city}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Postcode *</label>
                            <input
                                type="text"
                                value={postcode}
                                onChange={(e) => { setPostcode(e.target.value.toUpperCase()); if (fieldErrors.postcode) setFieldErrors(p => ({ ...p, postcode: undefined as any })); }}
                                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 ${fieldErrors.postcode ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ outlineColor: '#69BE28' }}
                            />
                            {fieldErrors.postcode && <p className="mt-1 text-sm text-red-600">{fieldErrors.postcode}</p>}
                        </div>
                    </div>
                </div>

                {/* Consent Information */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FileText className="w-5 h-5 mr-2" />
                        Consent Details
                    </h2>

                    <div className="space-y-4">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                            <div className="flex items-start">
                                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                                <div>
                                    <h3 className="font-medium text-yellow-800">D906 Consent Required</h3>
                                    <p className="text-sm text-yellow-800 mt-1">
                                        By law, we need your consent to check your DVLA driving licence information.
                                        This replaces the previous D796 form which was discontinued in August 2018.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="prose text-gray-700">
                            <h3 className="text-base font-medium">What this consent allows:</h3>
                            <ul className="text-sm space-y-1">
                                <li>• Check your current licence status and validity</li>
                                <li>• View your licence categories and entitlements</li>
                                <li>• Access penalty points and endorsements</li>
                                <li>• Monitor licence expiry dates</li>
                                <li>• Verify professional qualifications (CPC, tachograph)</li>
                            </ul>

                            <h3 className="text-base font-medium mt-4">Your rights:</h3>
                            <ul className="text-sm space-y-1">
                                <li>• This consent is valid for 3 years from today</li>
                                <li>• You can withdraw consent at any time</li>
                                <li>• Your data is processed in accordance with GDPR</li>
                                <li>• Information is only used for compliance purposes</li>
                            </ul>
                        </div>

                        <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            Consent valid until: {format(new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy')}
                        </div>
                    </div>
                </div>

                {/* Digital Signature */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Digital Signature</h2>

                    <div className="space-y-4">
                        <p className="text-gray-700">
                            Please sign below to provide your consent for DVLA licence checking:
                        </p>

                        <div ref={signatureContainerRef} className="border-2 border-gray-300 rounded-lg overflow-hidden">
                            <SignatureCanvas
                                key={canvasWidth}
                                ref={signatureRef}
                                onBegin={handleSignatureBegin}
                                onEnd={handleSignatureEnd}
                                canvasProps={{
                                    width: canvasWidth - 4,
                                    height: 200,
                                    className: 'signature-canvas block',
                                    style: { background: '#ffffff' }
                                }}
                                penColor="black"
                                minWidth={1}
                                maxWidth={3}
                            />
                        </div>

                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">
                                {hasSignature ? 'Signature captured' : 'Please sign above to continue'}
                            </p>

                            <button
                                type="button"
                                onClick={clearSignature}
                                className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-red-600 transition-colors"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                    <button
                        onClick={() => setShowWithdrawDialog(true)}
                        className="flex items-center justify-center px-6 py-3 border border-red-300 text-red-800 rounded-md hover:bg-red-50 transition-colors"
                    >
                        <XCircle className="w-5 h-5 mr-2" />
                        Decline Consent
                    </button>

                    <button
                        onClick={handleSubmitConsent}
                        disabled={!hasSignature || isSubmitting}
                        className={`flex items-center justify-center px-8 py-3 rounded-md font-medium transition-opacity ${hasSignature && !isSubmitting
                            ? 'text-white hover:opacity-90'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        style={hasSignature && !isSubmitting ? { backgroundColor: '#69BE28' } : undefined}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5 mr-2" />
                                Submit Consent
                            </>
                        )}
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex items-center">
                            <XCircle className="w-5 h-5 text-red-600 mr-2" />
                            <p className="text-red-800">{error}</p>
                        </div>
                    </div>
                )}

                {/* Withdrawal Dialog */}
                {showWithdrawDialog && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Decline Consent</h3>
                            <p className="text-gray-700 mb-4">
                                Are you sure you want to decline this consent request? Please provide a reason:
                            </p>

                            <textarea
                                value={withdrawalReason}
                                onChange={(e) => setWithdrawalReason(e.target.value)}
                                placeholder="Reason for declining (optional)"
                                className="w-full p-3 border border-gray-300 rounded-md resize-none"
                                rows={3}
                            />

                            <div className="flex gap-3 mt-4">
                                <button
                                    onClick={() => setShowWithdrawDialog(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleWithdrawConsent}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Decline Consent
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ConsentFormPage;